/**
 * SNES Sample Browser
 * Browse and play BRR samples from the SNES music archive
 */

import { decodeBrr } from '@/audio/BrrDecoder';
import { detectPitch } from '@/audio/PitchDetector';
import type { GameIndexEntry, GameFile } from '@/audio/sampleTypes';

export async function renderSampleBrowser(appElement: HTMLDivElement): Promise<void> {
  let audioCtx: AudioContext | null = null;
  let index: GameIndexEntry[] = [];
  let currentGame: GameFile | null = null;
  let decodedSamples: Map<number, { pcm: Int16Array; loopStart: number }> = new Map();

  appElement.className = 'scrollable-page';
  appElement.innerHTML = `
    <div style="padding: 20px; font-family: monospace; max-width: 1200px;">
      <h2>SNES Sample Browser</h2>
      <p>Browse and play BRR samples from SNES games.</p>

      <div style="margin: 20px 0; padding: 15px; background: #1a1a2e; border-radius: 8px;">
        <button id="init-btn" style="padding: 10px 20px; font-size: 16px; cursor: pointer;">
          Initialize Audio
        </button>
        <span id="status" style="margin-left: 15px; color: #888;">Not initialized</span>
      </div>

      <div id="main-content" style="display: none;">
        <div style="display: grid; grid-template-columns: 300px 300px 1fr; gap: 20px;">
          <!-- Game List -->
          <div style="background: #16213e; border-radius: 8px; padding: 15px;">
            <h3 style="margin-top: 0;">Games</h3>
            <input type="text" id="game-search" placeholder="Search games..."
              style="width: 100%; padding: 8px; margin-bottom: 10px; box-sizing: border-box;
                     background: #1a1a2e; border: 1px solid #333; color: #fff; border-radius: 4px;">
            <div id="game-list" style="height: 400px; overflow-y: auto;"></div>
            <div id="game-count" style="color: #666; font-size: 12px; margin-top: 10px;"></div>
          </div>

          <!-- Track List -->
          <div style="background: #16213e; border-radius: 8px; padding: 15px;">
            <h3 style="margin-top: 0;">Tracks</h3>
            <div id="track-list" style="height: 450px; overflow-y: auto;"></div>
          </div>

          <!-- Sample Grid -->
          <div style="background: #16213e; border-radius: 8px; padding: 15px;">
            <h3 style="margin-top: 0;">Samples <span id="sample-count" style="color: #666; font-size: 14px;"></span></h3>
            <div style="margin-bottom: 10px;">
              <label style="color: #888; font-size: 12px;">
                Pitch: <input type="range" id="pitch-slider" min="-12" max="12" value="0" style="width: 100px; vertical-align: middle;">
                <span id="pitch-value">0</span> semitones
              </label>
              <label style="color: #888; font-size: 12px; margin-left: 20px;">
                <input type="checkbox" id="loop-enabled" checked> Loop
              </label>
              <button id="stop-btn" style="margin-left: 20px; padding: 5px 15px; background: #c33; border: none; color: #fff; border-radius: 4px; cursor: pointer;">
                Stop
              </button>
            </div>
            <div id="sample-grid" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(80px, 1fr)); gap: 8px; max-height: 400px; overflow-y: auto;"></div>
          </div>
        </div>

        <!-- Waveform Display -->
        <div style="margin-top: 20px; background: #16213e; border-radius: 8px; padding: 15px;">
          <h3 style="margin-top: 0;">Waveform</h3>
          <canvas id="waveform" width="1100" height="150" style="background: #000; border: 1px solid #333; width: 100%;"></canvas>
          <div id="sample-info" style="color: #888; font-size: 12px; margin-top: 5px;"></div>
        </div>
      </div>

      <div id="loading" style="display: none; color: #888;">Loading...</div>
      <div id="error" style="display: none; color: #f66;"></div>
    </div>
  `;

  const initBtn = document.getElementById('init-btn') as HTMLButtonElement;
  const statusEl = document.getElementById('status') as HTMLSpanElement;
  const mainContent = document.getElementById('main-content') as HTMLDivElement;
  const loadingEl = document.getElementById('loading') as HTMLDivElement;
  const errorEl = document.getElementById('error') as HTMLDivElement;
  const gameSearch = document.getElementById('game-search') as HTMLInputElement;
  const gameList = document.getElementById('game-list') as HTMLDivElement;
  const gameCount = document.getElementById('game-count') as HTMLDivElement;
  const trackList = document.getElementById('track-list') as HTMLDivElement;
  const sampleGrid = document.getElementById('sample-grid') as HTMLDivElement;
  const sampleCount = document.getElementById('sample-count') as HTMLSpanElement;
  const pitchSlider = document.getElementById('pitch-slider') as HTMLInputElement;
  const pitchValue = document.getElementById('pitch-value') as HTMLSpanElement;
  const loopEnabled = document.getElementById('loop-enabled') as HTMLInputElement;
  const stopBtn = document.getElementById('stop-btn') as HTMLButtonElement;
  const waveformCanvas = document.getElementById('waveform') as HTMLCanvasElement;
  const sampleInfo = document.getElementById('sample-info') as HTMLDivElement;
  const waveformCtx = waveformCanvas.getContext('2d')!;

  // Currently playing source
  let currentSource: AudioBufferSourceNode | null = null;

  // Stop playback helper
  function stopPlayback(): void {
    if (currentSource) {
      currentSource.stop();
      currentSource = null;
    }
  }

  pitchSlider.addEventListener('input', () => {
    pitchValue.textContent = pitchSlider.value;
  });

  stopBtn.addEventListener('click', stopPlayback);

  // Load index
  async function loadIndex(): Promise<void> {
    loadingEl.style.display = 'block';
    loadingEl.textContent = 'Loading sample index...';

    try {
      const response = await fetch('/data/samples/index.json');
      if (!response.ok) {
        throw new Error(`Failed to load index: ${response.status}`);
      }
      index = await response.json();
      loadingEl.style.display = 'none';
      renderGameList();
    } catch (err) {
      loadingEl.style.display = 'none';
      errorEl.style.display = 'block';
      errorEl.textContent = `Error loading index: ${err}. Make sure to run ./dev-cli app build first.`;
    }
  }

  // Render game list
  function renderGameList(filter: string = ''): void {
    const filtered = filter
      ? index.filter(
          (g) =>
            g.title.toLowerCase().includes(filter.toLowerCase()) ||
            g.id.toLowerCase().includes(filter.toLowerCase())
        )
      : index;

    gameList.innerHTML = filtered
      .map(
        (g) => `
        <div class="game-item" data-id="${g.id}"
          style="padding: 8px; cursor: pointer; border-bottom: 1px solid #333;
                 transition: background 0.1s;">
          <div style="font-weight: bold; color: #fff;">${escapeHtml(g.title)}</div>
          <div style="font-size: 11px; color: #666;">
            <span style="color: #888; font-family: monospace;">$${g.id}</span> · ${g.tracks.length} tracks, ${g.sampleCount} samples
          </div>
        </div>
      `
      )
      .join('');

    gameCount.textContent = `${filtered.length} of ${index.length} games`;

    // Add click handlers
    gameList.querySelectorAll('.game-item').forEach((el) => {
      el.addEventListener('click', () => loadGame((el as HTMLElement).dataset.id!));
      el.addEventListener('mouseenter', () => ((el as HTMLElement).style.background = '#1a1a2e'));
      el.addEventListener('mouseleave', () => ((el as HTMLElement).style.background = ''));
    });
  }

  // Load game data
  async function loadGame(gameId: string): Promise<void> {
    loadingEl.style.display = 'block';
    loadingEl.textContent = `Loading ${gameId}...`;

    try {
      const response = await fetch(`/data/samples/games/${gameId}.json`);
      if (!response.ok) {
        throw new Error(`Failed to load game: ${response.status}`);
      }
      currentGame = await response.json();
      decodedSamples.clear();
      loadingEl.style.display = 'none';

      // Highlight selected game
      gameList.querySelectorAll('.game-item').forEach((el) => {
        (el as HTMLElement).style.background =
          (el as HTMLElement).dataset.id === gameId ? '#2a3a5e' : '';
      });

      renderTrackList();
      // Clear sample grid until track is selected
      sampleGrid.innerHTML = '<div style="color: #666;">Select a track to see its samples</div>';
      sampleCount.textContent = '';
    } catch (err) {
      loadingEl.style.display = 'none';
      errorEl.style.display = 'block';
      errorEl.textContent = `Error loading game: ${err}`;
    }
  }

  // Render track list
  function renderTrackList(): void {
    if (!currentGame) {
      trackList.innerHTML = '<div style="color: #666;">Select a game first</div>';
      return;
    }

    const tracks = Object.entries(currentGame.tracks);
    trackList.innerHTML = tracks
      .map(
        ([key, track]) => `
        <div class="track-item" data-key="${key}"
          style="padding: 8px; cursor: pointer; border-bottom: 1px solid #333;
                 transition: background 0.1s;">
          <div style="color: #fff;">${escapeHtml(track.name)}</div>
          <div style="font-size: 11px; color: #666;">${track.sampleIndices.length} samples</div>
        </div>
      `
      )
      .join('');

    // Add click handlers
    trackList.querySelectorAll('.track-item').forEach((el) => {
      el.addEventListener('click', () => selectTrack((el as HTMLElement).dataset.key!));
      el.addEventListener('mouseenter', () => ((el as HTMLElement).style.background = '#1a1a2e'));
      el.addEventListener('mouseleave', () => {
        if (!(el as HTMLElement).classList.contains('selected')) {
          (el as HTMLElement).style.background = '';
        }
      });
    });
  }

  // Select track and show its samples
  function selectTrack(trackKey: string): void {
    if (!currentGame) return;

    const track = currentGame.tracks[trackKey];
    if (!track) return;

    // Highlight selected track
    trackList.querySelectorAll('.track-item').forEach((el) => {
      const isSelected = (el as HTMLElement).dataset.key === trackKey;
      (el as HTMLElement).style.background = isSelected ? '#2a3a5e' : '';
      el.classList.toggle('selected', isSelected);
    });

    // Get unique sample indices for this track
    const uniqueIndices = [...new Set(track.sampleIndices)].sort((a, b) => a - b);
    sampleCount.textContent = `(${uniqueIndices.length} unique)`;

    // Render sample buttons with full reference format
    const gameId = currentGame!.id;
    sampleGrid.innerHTML = uniqueIndices
      .map(
        (idx) => `
        <button class="sample-btn" data-index="${idx}"
          style="padding: 15px 10px; background: #1a1a2e; border: 1px solid #444;
                 border-radius: 4px; color: #fff; cursor: pointer; font-family: monospace;
                 transition: all 0.1s; font-size: 11px;"
          title="$${gameId}[${idx}]">
          #${idx}
        </button>
      `
      )
      .join('');

    // Add mousedown handlers for immediate playback
    sampleGrid.querySelectorAll('.sample-btn').forEach((btn) => {
      btn.addEventListener('mousedown', () =>
        playSample(parseInt((btn as HTMLElement).dataset.index!))
      );
      btn.addEventListener('mouseenter', () => {
        (btn as HTMLElement).style.background = '#2a3a5e';
        (btn as HTMLElement).style.borderColor = '#666';
      });
      btn.addEventListener('mouseleave', () => {
        (btn as HTMLElement).style.background = '#1a1a2e';
        (btn as HTMLElement).style.borderColor = '#444';
      });
    });
  }

  // Decode and cache a sample
  function getDecodedSample(index: number): { pcm: Int16Array; loopStart: number } | null {
    if (!currentGame) return null;

    if (decodedSamples.has(index)) {
      return decodedSamples.get(index)!;
    }

    const sample = currentGame.samples[index];
    if (!sample) return null;

    // Decode base64 BRR data
    const brrData = Uint8Array.from(atob(sample.data), (c) => c.charCodeAt(0));
    const decoded = decodeBrr(brrData);

    // Calculate loop start in samples (loopOffset is in bytes)
    // Each 9-byte BRR block = 16 samples
    const loopBlock = Math.floor(sample.loopOffset / 9);
    const loopStart = loopBlock * 16;

    const result = { pcm: decoded.samples, loopStart };
    decodedSamples.set(index, result);
    return result;
  }

  // Draw waveform
  function drawWaveform(pcm: Int16Array, loopStart: number): void {
    const width = waveformCanvas.width;
    const height = waveformCanvas.height;
    const midY = height / 2;

    // Clear
    waveformCtx.fillStyle = '#000';
    waveformCtx.fillRect(0, 0, width, height);

    // Draw center line
    waveformCtx.strokeStyle = '#333';
    waveformCtx.beginPath();
    waveformCtx.moveTo(0, midY);
    waveformCtx.lineTo(width, midY);
    waveformCtx.stroke();

    if (pcm.length === 0) return;

    // Draw loop point
    if (loopStart > 0 && loopStart < pcm.length) {
      const loopX = (loopStart / pcm.length) * width;
      waveformCtx.strokeStyle = '#f00';
      waveformCtx.beginPath();
      waveformCtx.moveTo(loopX, 0);
      waveformCtx.lineTo(loopX, height);
      waveformCtx.stroke();

      waveformCtx.fillStyle = '#f00';
      waveformCtx.font = '10px monospace';
      waveformCtx.fillText('LOOP', loopX + 3, 12);
    }

    // Draw waveform
    waveformCtx.strokeStyle = '#0f0';
    waveformCtx.beginPath();

    const samplesPerPixel = pcm.length / width;
    for (let x = 0; x < width; x++) {
      const sampleIdx = Math.floor(x * samplesPerPixel);
      const sample = pcm[sampleIdx] / 32768; // Normalize to -1..1
      const y = midY - sample * midY * 0.9;

      if (x === 0) {
        waveformCtx.moveTo(x, y);
      } else {
        waveformCtx.lineTo(x, y);
      }
    }
    waveformCtx.stroke();
  }

  // Play a sample
  function playSample(index: number): void {
    if (!audioCtx) return;

    const decoded = getDecodedSample(index);
    if (!decoded) return;

    // Stop any currently playing sample
    stopPlayback();

    const { pcm, loopStart } = decoded;

    // Create audio buffer (SNES samples are 32kHz)
    const sampleRate = 32000;
    const buffer = audioCtx.createBuffer(1, pcm.length, sampleRate);
    const channelData = buffer.getChannelData(0);

    // Convert Int16 to Float32
    for (let i = 0; i < pcm.length; i++) {
      channelData[i] = pcm[i] / 32768;
    }

    // Create source
    const source = audioCtx.createBufferSource();
    source.buffer = buffer;

    // Apply pitch shift
    const semitones = parseInt(pitchSlider.value);
    source.playbackRate.value = Math.pow(2, semitones / 12);

    // Set loop if enabled and sample has loop point
    if (loopEnabled.checked && loopStart > 0 && loopStart < pcm.length) {
      source.loop = true;
      source.loopStart = loopStart / sampleRate;
      source.loopEnd = pcm.length / sampleRate;
    }

    source.connect(audioCtx.destination);
    source.start();
    currentSource = source;

    // Auto-stop after 5 seconds if looping
    if (source.loop) {
      setTimeout(() => {
        if (currentSource === source) {
          source.stop();
          currentSource = null;
        }
      }, 5000);
    }

    // Draw waveform
    drawWaveform(pcm, loopStart);

    // Detect pitch
    const pitchResult = detectPitch(pcm, { sampleRate });
    const pitchInfo = pitchResult
      ? `<span style="color: #f9a825;">base: ${pitchResult.noteName} (${pitchResult.nearestNote})</span>` +
        `${Math.abs(pitchResult.cents) > 10 ? ` <span style="color: #888;">${pitchResult.cents > 0 ? '+' : ''}${pitchResult.cents.toFixed(0)}¢</span>` : ''}`
      : '<span style="color: #888;">pitch: ?</span>';

    // Show info with symbolic reference
    const durationMs = (pcm.length / sampleRate) * 1000;
    const sizeBytes = currentGame?.samples[index]?.data
      ? Math.ceil((currentGame.samples[index].data.length * 3) / 4)
      : 0;
    const gameId = currentGame?.id ?? '?';
    sampleInfo.innerHTML =
      `<span style="color: #4ade80; font-weight: bold;">$${gameId}[${index}]</span>: ` +
      `${pcm.length} samples, ${durationMs.toFixed(1)}ms @ 32kHz, ` +
      `${sizeBytes} bytes BRR, loop at sample ${loopStart}, ${pitchInfo}`;
  }

  // Search handler
  gameSearch.addEventListener('input', () => {
    renderGameList(gameSearch.value);
  });

  // Stop playback on space
  document.addEventListener('keydown', (e) => {
    if (e.code === 'Space' && currentSource) {
      e.preventDefault();
      stopPlayback();
    }
  });

  // Initialize
  initBtn.addEventListener('click', async () => {
    try {
      statusEl.textContent = 'Initializing...';
      audioCtx = new AudioContext();
      await audioCtx.resume();

      statusEl.textContent = 'Ready!';
      statusEl.style.color = '#4ade80';
      initBtn.disabled = true;
      mainContent.style.display = 'block';

      await loadIndex();
    } catch (err) {
      statusEl.textContent = `Error: ${err}`;
      statusEl.style.color = '#f87171';
    }
  });

  // Draw empty waveform
  drawWaveform(new Int16Array(0), 0);
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
